/**
 * snapshotKeyPolicy — where snapshot key material is allowed to live.
 *
 * The ruling (ED session 2026-08-07): initial backup — and therefore the
 * minting and installation of the account root key, the recovery mnemonic
 * cache, and per-plan snapshot master keys — happens ONLY where a real
 * keychain exists:
 *
 *   - native iOS / Android (expo-secure-store → Keychain / Keystore)
 *   - the Electron desktop app (safeStorage → OS keychain via IPC)
 *
 * Plain browser web is excluded outright. The web SecureStore stub falls
 * back to plaintext localStorage — built for session tokens, and for a
 * while snapshot secrets rode it silently: the recovery mnemonic, the
 * account root key, and per-plan SMKs were all sitting in DevTools →
 * Application → Local Storage, on a screen whose copy said "Zero
 * plaintext" and "Biometric-gated". A browser cannot honor either claim,
 * so it doesn't get the keys.
 *
 * Everything here is deliberately dependency-light (Platform + globals)
 * so the crypto utilities can import it without cycles.
 */

import { Platform } from 'react-native';

/** True when this runtime has a real keychain for snapshot secrets. */
export function canHoldSnapshotKeys(): boolean {
  if (Platform.OS === 'ios' || Platform.OS === 'android') return true;
  if (Platform.OS === 'web') {
    // The Electron shell exposes safeStorage over IPC; plain browsers don't.
    return typeof window !== 'undefined' && !!(window as any).electronBridge;
  }
  return false;
}

/**
 * Thrown by the key utilities when a mint/install is attempted somewhere
 * keys must not live. Carries a user-ready message so action-layer callers
 * can surface it verbatim.
 */
export class SnapshotKeyPolicyError extends Error {
  constructor() {
    super(
      'Snapshot keys live in a device keychain. Set up Health Snapshots in the ' +
      'Eleplan mobile app or the Eleplan desktop app — this browser cannot store them safely.',
    );
    this.name = 'SnapshotKeyPolicyError';
  }
}

/**
 * Every SecureStore key the snapshot system has ever written starts with
 * `snap_` — including the pre-versioned SMK slot (`snap_smk_<projectId>`,
 * no `v2_`) and the synced root slot (`snap_account_root_synced_<id>`).
 * On a plain browser, ANY such key in localStorage is leaked secret
 * material by definition (SecureStore stores nothing else), so the sweep
 * matches the same prefix the stub's denylist refuses — one rule, both
 * layers, nothing left behind for the read-purge to catch one key at a
 * time. `snapshot_view_pref`-style neighbours don't match: the pattern
 * requires the underscore.
 */
export const LEAKED_KEY_PATTERN = /^snap_/;

/**
 * Remove any snapshot key material a previous build leaked into plaintext
 * localStorage on this browser. Electron is exempt — its entries live in
 * the OS keychain, not localStorage, so there is nothing to sweep.
 *
 * Returns the number of entries removed (0 where not applicable), and never
 * throws — hygiene must not take down the screen that runs it.
 */
export function sweepLeakedSnapshotKeys(): number {
  if (canHoldSnapshotKeys()) return 0;
  if (typeof localStorage === 'undefined') return 0;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && LEAKED_KEY_PATTERN.test(key)) doomed.push(key);
    }
    for (const key of doomed) {
      try { localStorage.removeItem(key); } catch { /* keep sweeping */ }
    }
    return doomed.length;
  } catch {
    return 0;
  }
}
